import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router"
import api from "../../lib/api"
import { getCurrentUser, hasCurrentPolicy } from "../../lib/auth"
import { getErrorMessage } from "../../lib/errors"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { TourButton } from "../../components/admin/TourButton"
import { teacherGroupTourSteps } from "../../lib/tours/teacherGroupTour"
import { LoadState } from "@/components/ui/LoadState"

type DashboardStats = {
  total_students?: number
  total_courses?: number
  total_enrollments?: number
  pending_submissions?: number
  average_progress?: number
  certificates_issued?: number
  quiz_attempts_today?: number
  pass_rate_today?: number
}

type DashboardData = {
  stats?: DashboardStats
  pending_submissions?: number
  [key: string]: unknown
}

type PendingSubmission = {
  id: string
  student_name: string
  project_title: string
  course_title: string
  submitted_at: string
  max_score: number
}

type PendingData = {
  submissions: PendingSubmission[]
  total: number
}

function formatAge(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime()
  const hours = Math.floor(diff / 3_600_000)
  if (hours < 1) return "hace menos de 1h"
  if (hours < 24) return `hace ${hours}h`
  return `hace ${Math.floor(hours / 24)}d`
}

export default function InstructorHub() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [pending, setPending] = useState<PendingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const user = getCurrentUser()
  const hour = new Date().getHours()
  const greeting =
    hour < 12 ? "Buenos días" : hour < 18 ? "Buenas tardes" : "Buenas noches"
  const today = new Date().toLocaleDateString("es-CO", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  })

  const load = useCallback(async () => {
    try {
      const [dashRes, pendRes] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/academy/projects/submissions/pending?limit=5"),
      ])
      setDashboard(dashRes.data.data ?? dashRes.data)
      setPending(pendRes.data.data ?? pendRes.data)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const stats: DashboardStats = (dashboard?.stats as DashboardStats) ?? dashboard ?? {}
  const pendingTotal = pending?.total ?? 0
  // La lista llega ordenada de la más antigua a la más reciente: la más antigua es la primera.
  const oldestPending = pending?.submissions[0]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Docentes", "Panel del instructor"]}
      title="Panel del instructor"
      subtitle="Tu centro de operaciones diario"
      tabs={[
        { label: "Panel", to: "/instructor/hub", active: true },
        { label: "Entregas", to: "/instructor/submissions" },
        { label: "Exámenes IA", to: "/instructor/section-quizzes" },
        { label: "Estudiantes", to: "/instructor/students" },
        { label: "Estadísticas", to: "/instructor/stats" },
      ]}
      metrics={[
        { label: "Entregas pendientes", value: String(stats.pending_submissions ?? pendingTotal) },
        { label: "Estudiantes activos", value: String(stats.total_students ?? "—") },
        {
          label: "Progreso promedio",
          value: stats.average_progress != null
            ? `${Math.round(stats.average_progress)}%`
            : "—",
        },
        { label: "Certificados emitidos", value: String(stats.certificates_issued ?? "—") },
      ]}
      toolbar={{ extra: <TourButton steps={teacherGroupTourSteps} /> }}
    >
      {loading && <LoadState />}
      {error && (
        <p role="alert" aria-live="assertive" className="text-error-600 dark:text-error-400">
          {error}
        </p>
      )}
      {!loading && !error && (
        <div className="space-y-6">
          {/* Greeting bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {greeting}, {user?.name}
            </h2>
            <span className="text-sm text-gray-500 capitalize">{today}</span>
          </div>

          {/* Sin pendientes: se reconoce y se ofrece el siguiente paso útil */}
          {pendingTotal === 0 && (
            <div className="rounded-2xl border border-success-200 bg-success-50 p-5 dark:border-success-500/30 dark:bg-success-500/10">
              <p className="text-base font-semibold text-gray-900 dark:text-white">No hay entregas por calificar</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Estás al día con las entregas.
                {hasCurrentPolicy("academy:my-group:view") ? (
                  <>
                    {" "}
                    <Link to="/instructor/my-group" className="font-medium text-brand-500 hover:underline dark:text-brand-400">
                      Revisa cómo va tu grupo →
                    </Link>
                  </>
                ) : null}
              </p>
            </div>
          )}

          {/* Hero CTA — hidden when 0 pending */}
          {pendingTotal > 0 && (
            <Link to="/instructor/submissions" className="block">
              <div className="rounded-2xl border border-warning-200 bg-warning-50 p-5 transition-colors hover:border-warning-300 dark:border-warning-500/30 dark:bg-warning-500/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-warning-700 dark:text-warning-300">Requieren tu atención</p>
                    <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">
                      {pendingTotal} entrega{pendingTotal !== 1 ? "s" : ""} pendiente
                      {pendingTotal !== 1 ? "s" : ""}
                    </p>
                    {oldestPending && (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        La más antigua: {formatAge(oldestPending.submitted_at)}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-sm font-medium text-warning-700 dark:text-warning-300">Calificar →</span>
                </div>
              </div>
            </Link>
          )}

          {/* Three-column feed */}
          <div data-tour="instructor-feed" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Entregas recientes */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                Entregas recientes
              </h3>
              {(pending?.submissions ?? []).length === 0 ? (
                <p className="text-sm text-gray-500">No hay entregas pendientes.</p>
              ) : (
                <ul className="space-y-3">
                  {(pending?.submissions ?? []).map((s) => (
                    <li key={s.id} className="flex justify-between items-start text-sm">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{s.student_name}</p>
                        <p className="text-gray-500">{s.project_title}</p>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0 ml-2">
                        {formatAge(s.submitted_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {pendingTotal > 5 && (
                <Link
                  to="/instructor/submissions"
                  className="mt-3 block py-1.5 text-sm text-brand-500 hover:underline dark:text-brand-400"
                >
                  Ver todas ({pendingTotal}) →
                </Link>
              )}
            </div>

            {/* Quizzes hoy */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Quizzes hoy</h3>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {stats.quiz_attempts_today ?? 0}
              </p>
              <p className="text-sm text-gray-500">intentos hoy</p>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                Tasa de aprobación:{" "}
                <span className="font-semibold">
                  {(stats.quiz_attempts_today ?? 0) > 0 ? `${Math.round(stats.pass_rate_today ?? 0)}%` : "—"}
                </span>
              </p>
            </div>

            {/* Alertas */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Estudiantes en riesgo</h3>
              <p className="text-sm text-gray-500">
                Abre la lista de estudiantes ya filtrada por quienes están en riesgo.
              </p>
              <Link
                to="/instructor/students?filter=at-risk"
                className="mt-3 block py-1.5 text-sm text-brand-500 hover:underline dark:text-brand-400"
              >
                Ver estudiantes en riesgo →
              </Link>
            </div>
          </div>
        </div>
      )}
    </ModuleLayout>
  )
}
